import React from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { ShieldCheck, Award, Briefcase, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CredibilitySection = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const cred = t.credibility;

  if (!cred) return null;

  const icons = [
    <ShieldCheck className="w-8 h-8 text-brand-primary" />,
    <Award className="w-8 h-8 text-emerald-500" />,
    <Briefcase className="w-8 h-8 text-purple-500" />
  ];

  return (
    <section className="py-20 md:py-32 bg-[#f4fcf9] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          
          {/* Left Column - Seal & Text */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left"
          >
            <p className="text-brand-primary font-bold tracking-wider uppercase text-sm mb-4">
              {cred.kicker}
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight mb-6">
              {cred.title}
            </h2>
            <p className="text-lg text-slate-600 mb-10 max-w-2xl">
              {cred.subtitle}
            </p>
            
            <img 
              src="/images/nc-board-seal.png" 
              alt="NC State Board of Dental Examiners Seal" 
              className="w-48 sm:w-56 h-auto opacity-90 mix-blend-multiply mb-10 drop-shadow-md"
            />

            <button 
              onClick={() => { navigate('/about'); setTimeout(() => document.getElementById('accreditation')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-full font-bold hover:bg-slate-800 transition-all hover:scale-105 shadow-lg shadow-slate-900/20"
            >
              {cred.btn}
              <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>

          {/* Right Column - Cards */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full lg:w-1/2 space-y-6"
          >
            {cred.cards.map((card, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-6 shadow-md border border-slate-100 flex items-start gap-6 hover:shadow-xl transition-all duration-300 hover:border-brand-primary/30 group"
              >
                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-blue-50 transition-colors">
                  {icons[idx]}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{card.title}</h3>
                  <p className="text-slate-600">{card.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default CredibilitySection;
